import React from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Instagram,
  Facebook,
  Linkedin,
  Twitter,
  Youtube,
  Clock,
  Edit2,
  ChevronRight
} from 'lucide-react';

interface ScheduledPost {
  id: string;
  content: string;
  platforms: string[];
  scheduledAt: string;
  status: 'scheduled' | 'suggested' | 'draft';
  aiGenerated?: boolean;
}

interface UpcomingPostsProps {
  posts?: ScheduledPost[];
  maxItems?: number;
  loading?: boolean;
  className?: string;
}

const getPlatformIcon = (platform: string) => {
  switch (platform.toLowerCase()) {
    case 'instagram':
      return <Instagram className="h-4 w-4" />;
    case 'facebook':
      return <Facebook className="h-4 w-4" />;
    case 'linkedin':
      return <Linkedin className="h-4 w-4" />;
    case 'twitter':
      return <Twitter className="h-4 w-4" />;
    case 'youtube':
      return <Youtube className="h-4 w-4" />;
    default:
      return null;
  }
};

const getPlatformColor = (platform: string) => {
  switch (platform.toLowerCase()) {
    case 'instagram':
      return 'text-pink-500';
    case 'facebook':
      return 'text-blue-600';
    case 'linkedin':
      return 'text-blue-700';
    case 'twitter':
      return 'text-gray-900 dark:text-white';
    case 'youtube':
      return 'text-red-600';
    default:
      return 'text-gray-500';
  }
};

const formatScheduledTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === tomorrow.toDateString();

  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  if (isToday) return `Hoje, ${time}`;
  if (isTomorrow) return `Amanhã, ${time}`;

  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const UpcomingPosts: React.FC<UpcomingPostsProps> = ({
  posts = [],
  maxItems = 3,
  loading = false,
  className = ''
}) => {
  const displayPosts = posts.slice(0, maxItems);

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center">
          <Calendar className="h-4 w-4 mr-2 text-gray-400" />
          Posts Agendados
        </h3>
        <Link
          to="/content"
          className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 flex items-center"
        >
          Ver todos
          <ChevronRight className="h-4 w-4 ml-1" />
        </Link>
      </div>

      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {loading ? (
          Array.from({ length: maxItems }).map((_, i) => (
            <div key={i} className="p-4 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-4 w-full bg-gray-200 dark:bg-gray-700 rounded mb-2" />
                  <div className="h-3 w-2/3 bg-gray-100 dark:bg-gray-600 rounded mb-2" />
                  <div className="flex space-x-2">
                    <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded" />
                    <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded" />
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : displayPosts.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Calendar className="h-10 w-10 mx-auto mb-2 opacity-50" />
            <p>Nenhum post agendado</p>
            <Link
              to="/content"
              className="mt-2 inline-block text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Criar post
            </Link>
          </div>
        ) : (
          displayPosts.map((post) => (
            <div
              key={post.id}
              className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white line-clamp-2">
                    {post.content}
                  </p>

                  <div className="flex items-center gap-3 mt-2">
                    {/* Platforms */}
                    <div className="flex items-center space-x-1">
                      {post.platforms.map((platform) => (
                        <span key={platform} className={getPlatformColor(platform)}>
                          {getPlatformIcon(platform)}
                        </span>
                      ))}
                    </div>

                    {/* Time */}
                    <span className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatScheduledTime(post.scheduledAt)}
                    </span>

                    {/* AI Badge */}
                    {post.aiGenerated && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded">
                        IA
                      </span>
                    )}

                    {/* Status */}
                    {post.status === 'suggested' && (
                      <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">
                        Sugerido
                      </span>
                    )}
                  </div>
                </div>

                <button className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                  <Edit2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UpcomingPosts;
